import ClockWidget from "./clockwidget";

const CUSTOM_TAG = 'pyro-clock';
const ATTR_WIDTH = 'width';
const ATTR_HEIGHT = 'height';
const ATTR_USESTYLESIZE = 'usestylesize';
const ATTR_CLOCKCLASS = 'clockclass';
const ATTR_SECONDS = 'seconds';

/**
 * process a CSS rules list
 * @param {HTMLElement} elm the DOM element
 * @param {CSSRuleList} rules CSS rules list
 * @param {Array<CSSStyleRule>} ret result array of CSSStyleRule instances
 */
function _processRules(elm, rules, ret) {
    const rc = rules ? (rules.length || 0) : 0;
    for ( let i=0 ; i < rc ; ++i ) {
        const rule = rules.item(i);
        console.debug("processing rule", rule);
        try {
            if ( (rule instanceof CSSMediaRule) && window.matchMedia(rule.conditionText).matches ) {
                _processRules(elm, rule.cssRules, ret);
            } else if ( (rule instanceof CSSSupportsRule) && CSS.supports(rule.conditionText) ) {
                _processRules(elm, rule.cssRules, ret);
            } else if ( rule instanceof CSSStyleRule ) {
                if ( elm.matches(rule.selectorText) ) {
                    console.debug("Match!");
                    ret.push(rule.style);
                }
            }
        } catch ( e ) {
            console.error(e);
        }
    }
}

/**
 * retrieves the pre-computed CSS rules
 * @param {HTMLElement} elm the DOM element
 * @returns {Array<CSSStyleRule>} array of CSSStyleRule instances
 */
function _getPreComputedStyle(elm) {
    let ret = [];
    const sheets = document.styleSheets || [];
    const sc = sheets.length || 0;
    for ( let i=0 ; i < sc ; ++i ) {
        const sheet = sheets.item(i);
        if ( sheet.cssRules && (sheet.cssRules.length > 0) ) {
            try {
                _processRules(elm, sheet.cssRules, ret);
            } catch ( e ) {
                console.error(e);
            }
        }
    }
    return ret;
}

/**
 * retrieves the element's size as specified by styles
 * @param {HTMLElement} elm 
 */
function _getSizeByStyle(elm) {
    const style = elm.style;
    const size = {
            width: style.width.length > 0 ? style.width : null,
            height: style.height.length > 0 ? style.height : null
        };
    if ( size.height === null || size.width === null ) {
        // go the long way...
        const css = _getPreComputedStyle(elm);
        const cc = css.length || 0;
        let has_width = size.width !== null;
        let has_height  = size.height !== null;
        if ( cc > 0 ) {
            for ( let i=cc-1 ; !has_width && !has_height && (i >= 0) ; --i ) {
                const rule = css[i];
                if ( !has_width && (rule.width.length > 0) ) {
                    size.width = rule.width;
                    has_width = true;
                }
                if ( !has_height && (rule.height.length > 0) ) {
                    size.height = rule.height;
                    has_height = true;
                }
            }
        }
    }
    // we don't want to return "null" values
    if ( size.width === null ) {
        size.width = '0';
    }
    if ( size.height === null ) {
        size.height = '0';
    }
    return size;
}

/**
 * custom DOM element providing a clock widget
 */
export default class ClockWidgetElement extends HTMLElement {

    /**
     * constructs a new instance
     */
    constructor() {
        super();
        // Create a shadow root
        this.attachShadow({mode: 'open'});
        this.host = null;
        this.clock = null;
    }
    
    /**
     * called after the element was connected to the DOM
     */
    connectedCallback() {
        // force "block" display
        this.style.display = 'block';
        // create the host element
        const host = document.createElement('div');
        host.style.display = 'block';
        host.style.overflow = 'hidden';
        // get initial size
        let width = '0';
        let height = '0';
        // check whether the size should be taken from CSS
        const usecss = this.hasAttribute(ATTR_USESTYLESIZE) ? ('true' === this.getAttribute(ATTR_USESTYLESIZE)) : false;
        if ( usecss ) {
            const size = _getSizeByStyle(this);
            console.debug("Size by style", size);
            width = size.width;
            height = size.height;
        } else {
            // get width and height from attributes
            width = this.hasAttribute(ATTR_WIDTH) ? this.getAttribute(ATTR_WIDTH) : '0';
            height = this.hasAttribute(ATTR_HEIGHT) ? this.getAttribute(ATTR_HEIGHT) : '0';
        }
        // set initial size of host element
        host.style.width = width;
        host.style.height = height;
        // create the actual clock widget
        const csc = this.hasAttribute(ATTR_CLOCKCLASS) ? this.getAttribute(ATTR_CLOCKCLASS) : '';
        this.clock = new ClockWidget(this, host, csc);
        const seconds = this.hasAttribute(ATTR_SECONDS) ? this.getAttribute(ATTR_SECONDS) : 'true';
        this.clock.setDisplaySeconds('true' === seconds);
        this.shadowRoot.appendChild(host);
    }

    /**
     * called if the value of an observed attribute was changed
     * @param {String} name attribute name
     * @param {String} oldValue previous value
     * @param {String} newValue new value
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if ( this.clock ) {
            console.debug(`Value of attribute "${name}" changed from "${oldValue}" to "${newValue}".`);
            switch ( name ) {
                case ATTR_WIDTH:
                    this.clock.setWidth(newValue);
                    break;
                case ATTR_HEIGHT:
                    this.clock.setHeight(newValue);
                    break;
                case ATTR_SECONDS:
                    this.clock.setDisplaySeconds('true' === newValue);
                    break;
            }
        }
    }

    /**
     * returns the list of observed attributes
     * @returns {String[]} the list of observerd attributes
     */
    static get observedAttributes() { 
        return [ ATTR_WIDTH, ATTR_HEIGHT ]; 
    }

	/**
	 * @returns {String} the custom tag name
	 */
     static get customTag() {
		return CUSTOM_TAG;
	}

}
